/**
 * Feature Registry
 * Single source of truth for what each plan tier includes.
 *
 * Adding a new feature: add it to PlanFeature union type in plan.types.ts,
 * then TypeScript will enforce you add an entry here for every tier.
 *
 * Adding a new tier: add it to PlanTier union in billing.types.ts,
 * then TypeScript will enforce you add an entry here for every feature.
 */

import { FeatureRegistry } from '../types/plan.types';

export const FEATURE_REGISTRY: FeatureRegistry = {
  ai_dictation: {
    free:       { enabled: true,  gateType: 'boolean' },
    pro:        { enabled: true,  gateType: 'boolean' },
    pair:       { enabled: true,  gateType: 'boolean' },
    enterprise: { enabled: true,  gateType: 'boolean' },
  },

  standard_templates: {
    free:       { enabled: true,  gateType: 'boolean' },
    pro:        { enabled: true,  gateType: 'boolean' },
    pair:       { enabled: true,  gateType: 'boolean' },
    enterprise: { enabled: true,  gateType: 'boolean' },
  },

  advanced_templates: {
    free:       { enabled: false, gateType: 'boolean' },
    pro:        { enabled: true,  gateType: 'boolean' },
    pair:       { enabled: true,  gateType: 'boolean' },
    enterprise: { enabled: true,  gateType: 'boolean' },
  },

  // user_custom_templates -- gates the /templates builder UI. Server-side
  // mirror is `Feature.CUSTOM_TEMPLATES` in app/tiers.py and the
  // `max_user_templates` quota in TIER_LIMITS. FREE users can build 1
  // template to try the feature; PRO+ are unlimited. The API enforces the
  // count cap and returns 403 `template_quota_exceeded` if they try to add
  // a second one.
  user_custom_templates: {
    free:       { enabled: true,  gateType: 'quota', limit: 1, period: 'monthly' },
    pro:        { enabled: true,  gateType: 'boolean' },
    pair:       { enabled: true,  gateType: 'boolean' },
    enterprise: { enabled: true,  gateType: 'boolean' },
  },

  // user_custom_question_schemas — Parallel Clinic intake-script builder
  // gate. Mirrors `Feature.CUSTOM_QUESTION_SCHEMAS` in app/tiers.py and the
  // `max_user_question_schemas` quota in TIER_LIMITS. FREE users can build
  // 1 schema; PRO+ are unlimited. The API enforces the count cap and
  // returns 403 `question_schema_quota_exceeded` if they try a second one.
  user_custom_question_schemas: {
    free:       { enabled: true,  gateType: 'quota', limit: 1 },
    pro:        { enabled: true,  gateType: 'boolean' },
    pair:       { enabled: true,  gateType: 'boolean' },
    enterprise: { enabled: true,  gateType: 'boolean' },
  },

  // ask_charti -- server-side mirror is `Feature.ASK_CHARTI` in
  // app/tiers.py and the `max_ask_charti_queries_monthly` quota in
  // TIER_LIMITS. Enforced by the API; this client gate is for UX only
  // (used by usePlanFeature to render the meter / paywall).
  ask_charti: {
    free:       { enabled: true,  gateType: 'quota', limit: 10,  period: 'monthly' },
    pro:        { enabled: true,  gateType: 'quota', limit: 200, period: 'monthly' },
    pair:       { enabled: true,  gateType: 'quota', limit: 200, period: 'monthly' },
    enterprise: { enabled: true,  gateType: 'quota' },
  },

  priority_support: {
    free:       { enabled: false, gateType: 'boolean' },
    pro:        { enabled: true,  gateType: 'boolean' },
    pair:       { enabled: true,  gateType: 'boolean' },
    enterprise: { enabled: true,  gateType: 'boolean' },
  },

  usage_analytics: {
    free:       { enabled: false, gateType: 'boolean' },
    pro:        { enabled: true,  gateType: 'boolean' },
    pair:       { enabled: true,  gateType: 'boolean' },
    enterprise: { enabled: true,  gateType: 'boolean' },
  },

  emr_integration: {
    free:       { enabled: false, gateType: 'boolean' },
    pro:        { enabled: false, gateType: 'boolean' },
    pair:       { enabled: false, gateType: 'boolean' },
    enterprise: { enabled: true,  gateType: 'boolean' },
  },

  api_access: {
    free:       { enabled: false, gateType: 'boolean' },
    pro:        { enabled: false, gateType: 'boolean' },
    pair:       { enabled: false, gateType: 'boolean' },
    enterprise: { enabled: true,  gateType: 'boolean' },
  },
};
